import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    ContextualField,
    Group,
    KeyValuePair,
    PropertyType,
    ScriptTestMode,
    ScriptType
} from '../../../../models/index';
import { IGroupScriptForm } from '../IGroupScriptForm';
import { IFileManagementService, IGroupService, ILoggerService, IStoreService } from '../../../../services';

class ModernGroupScriptContextualController implements angular.IComponentController {
    formController: IGroupScriptForm;
    isCodeEditorDisabled: boolean;
    properties: ContextualField[];

    private _libraries: { [key: number]: KeyValuePair<string, string>[] }; // key is of type ScriptType
    private _originalLibraryCollection: string[];

    readonly group: Group;
    readonly outsideFormController: angular.IFormController;
    readonly scriptTestMode: ScriptTestMode;

    static $inject = [
        '$q',
        '$state',
        'fileManagementService',
        'groupService',
        'translatedLoggerService',
        'storeService'
    ];
    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _fileManagementService: IFileManagementService,
        private _groupService: IGroupService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
    ) {
    }

    $onInit() {
        this._$q.all([
            this._fileManagementService.getAllScriptLibraries(ScriptType.PythonV2),
            this._fileManagementService.getAllScriptLibraries(ScriptType.PythonV3)
        ])
            .then(data => {
                try {
                    const libs = {};
                    data.forEach((d, i) => {
                        libs[i] = d.map(str => {
                            return { key: str.toLowerCase(), value: str };
                        });
                    });
                    this._libraries = libs;
                    const librariesField: ContextualField = {
                        label: 'Libraries',
                        modelName: 'libraryCollection',
                        propertyName: 'libraryCollectionProperty',
                        propertyType: PropertyType.MultiSelect,
                        required: true,
                        iconText: '.py',
                        extraData: {
                            onClick: (value: string) => this._$state.go('fileManagement', { type: this.group.scriptType, file: `${value}.py` })
                        },
                        onChange: () => {
                            this.isCodeEditorDisabled = true;
                        }
                    };
                    const pythonVersionField: ContextualField = {
                        label: 'Python version',
                        modelName: 'scriptType',
                        propertyName: 'scriptTypeProperty',
                        propertyType: PropertyType.Select,
                        required: true,
                        icon: ['fa-code', 'fa-code-fork'],
                        extraData: {
                            options: [
                                { key: ScriptType.PythonV2, value: '2.x' },
                                { key: ScriptType.PythonV3, value: '3.x' }
                            ]
                        },
                        onChange: (value: ScriptType) => {
                            const libraries = this._libraries[value];
                            librariesField.extraData.options = libraries;
                            this.group.libraryCollection = _.intersection(this.group.libraryCollection, libraries.map(l => l.key));
                            this.isCodeEditorDisabled = true;
                        }
                    };
                    if (this.group) {
                        const libraries = this._libraries[this.group.scriptType];
                        librariesField.extraData.options = libraries;
                        this.group.libraryCollection = _.intersection(this.group.libraryCollection, libraries.map(l => l.key));
                    }
                    this.properties = [
                        pythonVersionField,
                        librariesField
                    ];
                } catch (e) {
                    this._loggerService.logError(e);
                }
            })
            .catch(e => {
                this._loggerService.logError(e);
            });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['group'] && this.group) {
            this._originalLibraryCollection = this.group.libraryCollection.map(d => d);
        }
    }

    onSave(): angular.IPromise<any> {
        return this._groupService
            .updateLibraries(this.group.guid, this.group.libraryCollection, this.group.script, this.group.scriptType)
            .then(data => {
                this._loggerService.logSuccess('Libraries successfully updated.');
                this.group.isMatrixUpToDate = false;
                if(this.outsideFormController){
                    this.outsideFormController.$setPristine();
                }
                this._storeService.dispatchers().groups.forceInMemoryUpdate(this.group);
                this._originalLibraryCollection = this.group.libraryCollection.map(d => d);
                this.isCodeEditorDisabled = false;
            })
            .catch(() => this._loggerService.logError('Failed to update libraries.'));
    }

    onCancel() {
        this.group.libraryCollection = this._originalLibraryCollection?.map(d => d) || [];
        const fields = this.properties
            .filter(p => p.modelName === 'libraryCollection');
        if (fields.length) {
            fields[0].extraData.options = this._libraries[this.group.scriptType];
        }
        this.isCodeEditorDisabled = false;
        if(this.outsideFormController){
            this.outsideFormController.$setPristine();
        }
    }
}

export const wtModernGroupScriptContextual: ComponentDefinition = {
    componentId: 'wtModernGroupScriptContextual',
    controller: ModernGroupScriptContextualController,
    template: require('./modernGroupScriptContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        group: '<',
        isCodeEditorDisabled: '=',
        outsideFormController: '<?',
        scriptTestMode: '<'
    }
};
